import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    User,
    Mail,
    Lock,
    Phone,
    CreditCard,
    Camera,
    Upload,
    Briefcase,
    Building2,
    ArrowLeft,
    ArrowRight,
    Check,
    Eye,
    EyeOff,
    X,
} from "lucide-react";
import { register, updateProfile } from "../../services/authService";
import { uploadAvatar, uploadDocument } from "../../services/cloudinaryService";
import Toast from '../../Components/Toast/Toast';
import GoldOnboardingModal from "../../Components/GoldOnboardingModal/GoldOnboardingModal";
import "./Register.scss";

export default function Register() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [phone, setPhone] = useState("");
    const [cpf, setCpf] = useState("");

    const [gender, setGender] = useState("");
    const [skills, setSkills] = useState([]);
    const [selectedSkill, setSelectedSkill] = useState("");
    const [workExperiences, setWorkExperiences] = useState([]);
    const [profileImageFile, setProfileImageFile] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState(null);

    const [documentType, setDocumentType] = useState("");
    const [documentFrontFile, setDocumentFrontFile] = useState(null);
    const [documentBackFile, setDocumentBackFile] = useState(null);

    const [submitting, setSubmitting] = useState(false);
    const [showGoldWelcome, setShowGoldWelcome] = useState(false);

    // Estados para mostrar/ocultar senha
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [phoneError, setPhoneError] = useState(null);
    const [phoneValidated, setPhoneValidated] = useState(false);
    const [cpfError, setCpfError] = useState("");

    const [toast, setToast] = useState({ message: "", type: "success" });

    const showToast = (message, type = "success") => {
        setToast({ message, type });
    };

    const validatePassword = (value) => {
        const strongPasswordRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

        if (!strongPasswordRegex.test(value)) {
            return "A senha deve conter pelo menos 8 caracteres, incluindo uma letra maiúscula, uma minúscula, um número e um caractere especial.";
        }
        return null;
    };
    const handleGoToStep2 = () => {
        if (!phoneValidated) {
            showToast("Valide seu telefone antes de continuar.", "error");
            return;
        }

        if (!validateCpf(cpf)) {
            setCpfError("CPF inválido. Verifique os números e tente novamente.");
            return;
        }

        setCpfError("");

        const passwordError = validatePassword(password);
        if (passwordError) {
            showToast(passwordError, "error");
            return;
        }

        if (password !== confirmPassword) {
            showToast("As senhas não coincidem.", "error");
            return;
        }

        setStep(2);
    };

    const validateCpf = (cpf) => {
        const numbers = cpf.replace(/\D/g, "");

        if (numbers.length !== 11) {
            return false;
        }

        if (/^(\d)\1{10}$/.test(numbers)) {
            return false;
        }

        let sum = 0;

        for (let i = 0; i < 9; i++) {
            sum += Number(numbers[i]) * (10 - i);
        }

        let remainder = sum % 11;
        let digit1 = remainder < 2 ? 0 : 11 - remainder;

        if (digit1 !== Number(numbers[9])) {
            return false;
        }

        sum = 0;

        for (let i = 0; i < 10; i++) {
            sum += Number(numbers[i]) * (11 - i);
        }

        remainder = sum % 11;
        let digit2 = remainder < 2 ? 0 : 11 - remainder;

        return digit2 === Number(numbers[10]);
    };

    const handlePhoneChange = (e) => {
        setPhone(formatPhone(e.target.value));
        setPhoneValidated(false);
        setPhoneError(null);
    };

    const handleValidatePhone = () => {
        const error = validatePhone(phone);
        if (error) {
            setPhoneError(error);
            setPhoneValidated(false);
        } else {
            setPhoneError(null);
            setPhoneValidated(true);
        }
    };
    // DDDs válidos no Brasil
    const VALID_DDDS = [
        "11", "12", "13", "14", "15", "16", "17", "18", "19",
        "21", "22", "24", "27", "28",
        "31", "32", "33", "34", "35", "37", "38",
        "41", "42", "43", "44", "45", "46", "47", "48", "49",
        "51", "53", "54", "55",
        "61", "62", "63", "64", "65", "66", "67", "68", "69",
        "71", "73", "74", "75", "77", "79",
        "81", "82", "83", "84", "85", "86", "87", "88", "89",
        "91", "92", "93", "94", "95", "96", "97", "98", "99",
    ];

    const formatPhone = (value) => {
        const digits = value.replace(/\D/g, "").slice(0, 11);
        if (digits.length <= 2) return digits;
        if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    };

    const validatePhone = (value) => {
        const digits = value.replace(/\D/g, "");
        if (digits.length !== 11) {
            return "O número precisa ter DDD + 9 dígitos (celular).";
        }
        const ddd = digits.slice(0, 2);
        if (!VALID_DDDS.includes(ddd)) {
            return "DDD inválido.";
        }
        if (digits[2] !== "9") {
            return "Número de celular deve começar com 9 após o DDD.";
        }
        return null; // válido
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setProfileImageFile(file);
        setProfileImagePreview(URL.createObjectURL(file));
    };

    const handleDocumentFrontChange = (e) => {
        const file = e.target.files?.[0];
        if (file) setDocumentFrontFile(file);
    };

    const handleDocumentBackChange = (e) => {
        const file = e.target.files?.[0];
        if (file) setDocumentBackFile(file);
    };

    const handleAddSkill = () => {
        const skill = selectedSkill.trim();
        if (!skill) return;
        setSkills((prev) => (prev.includes(skill) ? prev : [...prev, skill]));
        setSelectedSkill("");
    };

    const handleRemoveSkill = (skillToRemove) => {
        setSkills((prev) => prev.filter((skill) => skill !== skillToRemove));
    };

    const handleAddExperience = () => {
        setWorkExperiences((prev) => [
            ...prev,
            { id: Math.random().toString(36).substr(2, 9), companyName: "", position: "", workMonth: "", workYear: "" },
        ]);
    };

    const handleUpdateExperience = (id, field, value) => {
        setWorkExperiences((prev) => prev.map((exp) => (exp.id === id ? { ...exp, [field]: value } : exp)));
    };

    const handleRemoveExperience = (id) => {
        setWorkExperiences((prev) => prev.filter((exp) => exp.id !== id));
    };

    //para tratamento de erros
    const getErrorMessage = (error) => {
        const data = error?.response?.data;

        if (!data) return "Erro ao cadastrar. Tente novamente.";
        if (typeof data === "string") return data;
        if (data.message) return data.message;
        if (data.error) return data.error;

        if (data.errors) {
            // ASP.NET Identity / ModelState: { errors: { Campo: ["msg1", "msg2"] } }
            const messages = Object.values(data.errors).flat();
            if (messages.length > 0) return messages.join(" ");
        }

        return "Erro ao cadastrar. Tente novamente.";
    };

    const handleRegister = async () => {
        if (!phoneValidated) {
            showToast(
                "É necessário validar o telefone antes de finalizar o cadastro.",
                "error"
            );
            return;
        }

        if (password !== confirmPassword) {
            showToast("As senhas não coincidem", "error");
            return;
        }

        setSubmitting(true);

        try {
            const result = await register({
                name,
                email,
                password,
                confirmPassword,
                userType: 1,
                cpf,
                phone,
            });

            // Guarda a sessão pro resto do app reconhecer o usuário logado
            localStorage.setItem("token", result.token);
            localStorage.setItem("userId", result.id);

            // Atualiza perfil com competências e experiências
            const uploadFailures = [];
            try {
                await updateProfile(result.id, {
                    name,
                    phone,
                    cpf,
                    gender,
                    areas: skills,
                    workExperiences: workExperiences.map(({ companyName, position, workMonth, workYear }) => ({
                        companyName,
                        position,
                        workMonth,
                        workYear,
                    })),
                });
            } catch (err) {
                console.error("Erro ao atualizar perfil:", err);
                uploadFailures.push("competências/experiências");
            }

            // A conta já existe agora — sobe os arquivos que ficaram esperando

            if (profileImageFile) {
                try {
                    await uploadAvatar(result.id, profileImageFile);
                } catch (err) {
                    console.error("Erro ao enviar avatar:", err);
                    uploadFailures.push("foto de perfil");
                }
            }

            if (documentType && documentFrontFile) {
                try {
                    await uploadDocument(result.id, documentFrontFile, documentType, "front");
                } catch (err) {
                    console.error("Erro ao enviar documento (frente):", err);
                    uploadFailures.push("documento (frente)");
                }
            }

            if (documentType && documentBackFile) {
                try {
                    await uploadDocument(result.id, documentBackFile, documentType, "back");
                } catch (err) {
                    console.error("Erro ao enviar documento (verso):", err);
                    uploadFailures.push("documento (verso)");
                }
            }

            if (uploadFailures.length > 0) {
                showToast(
                    `Cadastro realizado, mas não foi possível enviar: ${uploadFailures.join(", ")}. ` +
                    "Você pode reenviar depois em Editar Perfil.", "info"
                );
            } else {
                showToast("Cadastro realizado com sucesso!", "success");
            }

            setShowGoldWelcome(true);
        } catch (error) {
            // console.log("erro do backend:", error?.response?.data);
            showToast(getErrorMessage(error), "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="register-page">
            <div className="register-overlay" />

            <div className="register-card">
                <div className="register-header">
                    <h1>Crie sua conta como Freelancer</h1>
                    <p>Encontre oportunidades..</p>

                    <div className="progress-container">
                        <div className="progress-info">
                            <span>Passo {step} de 3</span>
                        </div>

                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{
                                    width: `${(step / 3) * 100}%`,
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="auth-tabs">
                    <Link to="/" className="tab-btn">
                        Entrar
                    </Link>

                    <button className="tab-btn active">
                        Cadastrar
                    </button>
                </div>

                {/* PASSO 1 */}
                {step === 1 && (
                    <div className="register-step">
                        <h2>Dados Básicos</h2>

                        <div className="form-group">
                            <label>Email *</label>

                            <div className="input-wrapper">
                                <Mail size={18} />

                                <input
                                    type="email"
                                    placeholder="seu@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Nome Completo *</label>

                            <div className="input-wrapper">
                                <User size={18} />

                                <input
                                    type="text"
                                    placeholder="Ex: João Silva"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Senha *</label>

                            <div className="input-wrapper">
                                <Lock size={18} />

                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Crie uma senha forte"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />

                                <button
                                    type="button"
                                    className="input-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Confirmar Senha *</label>

                            <div className="input-wrapper">
                                <Lock size={18} />

                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Digite a mesma senha novamente"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />

                                <button
                                    type="button"
                                    className="input-toggle"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    aria-label={showConfirmPassword ? "Ocultar senha" : "Mostrar senha"}
                                >
                                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Telefone (WhatsApp) *</label>

                            <div className="phone-group">
                                <div className="input-wrapper">
                                    <Phone size={18} />
                                    <input
                                        type="text"
                                        placeholder="(11) 99999-9999"
                                        value={phone}
                                        onChange={handlePhoneChange}
                                    />
                                </div>

                                <button
                                    type="button"
                                    className={`btn-validate ${phoneValidated ? "is-valid" : ""}`}
                                    onClick={handleValidatePhone}
                                    disabled={phoneValidated}
                                >
                                    <Check size={16} />
                                    {phoneValidated ? "Validado" : "Validar"}
                                </button>
                            </div>

                            {phoneError && <small className="field-error">{phoneError}</small>}
                            {phoneValidated && <small className="field-success">Número válido</small>}
                            {!phoneError && !phoneValidated && <small>Requer validação</small>}
                        </div>

                        <div className="form-group">
                            <label>CPF *</label>

                            <div className={`input-wrapper ${cpfError ? "input-wrapper--error" : ""}`}>
                                <CreditCard size={18} />

                                <input
                                    type="text"
                                    placeholder="000.000.000-00"
                                    value={cpf}
                                    maxLength={14}
                                    onChange={(e) => {
                                        const value = e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, 11);

                                        const masked = value
                                            .replace(/(\d{3})(\d)/, "$1.$2")
                                            .replace(/(\d{3})(\d)/, "$1.$2")
                                            .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

                                        setCpf(masked);

                                        if (cpfError) {
                                            setCpfError("");
                                        }
                                    }}
                                />
                            </div>

                            {cpfError && (
                                <span className="form-error">
                                    {cpfError}
                                </span>
                            )}
                        </div>

                        <button className="btn-next" onClick={handleGoToStep2}>
                            Próximo
                            <ArrowRight size={18} />
                        </button>
                    </div>
                )}

                {/* PASSO 2 */}
                {step === 2 && (
                    <div className="register-step">
                        <h2>Perfil Profissional</h2>

                        <div className="form-group">
                            <label>Foto de Perfil</label>

                            <label className="upload-box" htmlFor="avatar-upload-input">
                                {profileImagePreview ? (
                                    <img
                                        src={profileImagePreview}
                                        alt="Preview"
                                        style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover" }}
                                    />
                                ) : (
                                    <Camera size={32} />
                                )}

                                <span>{profileImageFile ? profileImageFile.name : "Selecionar Imagem"}</span>

                                <input id="avatar-upload-input" type="file" accept="image/*" onChange={handleAvatarChange} />
                            </label>
                        </div>

                        <div className="form-group">
                            <label>Gênero *</label>

                            <div className="input-wrapper">
                                <User size={18} />

                                <select
                                    value={gender}
                                    onChange={(e) => setGender(e.target.value)}
                                >
                                    <option value="">Selecione seu gênero...</option>
                                    <option value="masculino">Masculino</option>
                                    <option value="feminino">Feminino</option>
                                    <option value="prefiro-nao-informar">Prefiro não informar</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>
                                Competências / Áreas de Atuação
                            </label>

                            <div className="skills-list">
                                {skills.length === 0 ? (
                                    <span className="skills-empty">
                                        <Briefcase size={20} />
                                        Nenhuma competência adicionada ainda.
                                    </span>
                                ) : (
                                    <div className="skills-chips">
                                        {skills.map((skill) => (
                                            <span key={skill} className="skill-chip">
                                                {skill}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveSkill(skill)}
                                                    aria-label="Remover competência"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="skill-add">
                                <div className="input-wrapper">
                                    <Briefcase size={18} />

                                    <select
                                        value={selectedSkill}
                                        onChange={(e) => setSelectedSkill(e.target.value)}
                                    >
                                        <option value="">
                                            Selecione uma profissão...
                                        </option>

                                        <optgroup label="🍽️ Restaurante e Bar">
                                            <option value="Garçom">Garçom</option>
                                            <option value="Garçonete">Garçonete</option>
                                            <option value="Cumim">Cumim</option>
                                            <option value="Bartender">Bartender</option>
                                            <option value="Barman">Barman</option>
                                            <option value="Barback">Barback</option>
                                            <option value="Caixa">Caixa</option>
                                            <option value="Recepcionista">Recepcionista</option>
                                            <option value="Hostess">Hostess</option>
                                        </optgroup>

                                        <optgroup label="👨‍🍳 Cozinha">
                                            <option value="Cozinheiro">Cozinheiro</option>
                                            <option value="Auxiliar de Cozinha">Auxiliar de Cozinha</option>
                                            <option value="Churrasqueiro">Churrasqueiro</option>
                                            <option value="Pizzaiolo">Pizzaiolo</option>
                                            <option value="Confeiteiro">Confeiteiro</option>
                                            <option value="Sushiman">Sushiman</option>
                                        </optgroup>

                                        <optgroup label="🧹 Serviços">
                                            <option value="Auxiliar de Limpeza">Auxiliar de Limpeza</option>
                                            <option value="Faxineiro(a)">Faxineiro(a)</option>
                                            <option value="Segurança">Segurança</option>
                                            <option value="Porteiro">Porteiro</option>
                                            <option value="Manobrista">Manobrista</option>
                                            <option value="Carregador">Carregador</option>
                                            <option value="Montador de Eventos">Montador de Eventos</option>
                                        </optgroup>

                                        <optgroup label="🚚 Logística">
                                            <option value="Motoboy">Motoboy</option>
                                            <option value="Motorista">Motorista</option>
                                            <option value="Entregador">Entregador</option>
                                        </optgroup>

                                        <optgroup label="🎉 Eventos">
                                            <option value="Promotor(a)">Promotor(a)</option>
                                            <option value="Monitor(a)">Monitor(a)</option>
                                            <option value="Animador(a)">Animador(a)</option>
                                            <option value="Atendente">Atendente</option>
                                            <option value="Apoio de Evento">Apoio de Evento</option>
                                        </optgroup>

                                        <option value="Outros">Outros</option>
                                    </select>
                                </div>

                                <button type="button" onClick={handleAddSkill}>
                                    <Check size={16} />
                                    Adicionar
                                </button>
                            </div>

                            <small>Selecione uma competência e clique em adicionar para montar sua lista.</small>
                        </div>

                        <div className="form-group">
                            <label>Experiências Profissionais</label>
                            <button type="button" className="btn-validate" onClick={handleAddExperience}>
                                <Check size={16} />
                                Adicionar Experiência
                            </button>

                            {workExperiences.length === 0 ? (
                                <span className="skills-empty">Nenhuma experiência adicionada ainda.</span>
                            ) : (
                                workExperiences.map((exp) => (
                                    <div key={exp.id} className="experience-card">
                                        <div className="experience-card__header">
                                            <span>Experiência</span>
                                            <button type="button" onClick={() => handleRemoveExperience(exp.id)}>
                                                <X size={14} />
                                            </button>
                                        </div>
                                        <div className="experience-grid">
                                            <div className="form-group">
                                                <label>Empresa / Local</label>
                                                <input
                                                    type="text"
                                                    value={exp.companyName}
                                                    onChange={(e) => handleUpdateExperience(exp.id, "companyName", e.target.value)}
                                                    placeholder="Nome do local"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Cargo / Função</label>
                                                <input
                                                    type="text"
                                                    value={exp.position}
                                                    onChange={(e) => handleUpdateExperience(exp.id, "position", e.target.value)}
                                                    placeholder="Sua função"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Mês</label>
                                                <input
                                                    type="text"
                                                    value={exp.workMonth}
                                                    onChange={(e) => handleUpdateExperience(exp.id, "workMonth", e.target.value)}
                                                    placeholder="Ex: Janeiro"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Ano</label>
                                                <input
                                                    type="text"
                                                    value={exp.workYear}
                                                    onChange={(e) => handleUpdateExperience(exp.id, "workYear", e.target.value)}
                                                    placeholder="Ex: 2023"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="step-actions">
                            <button
                                className="btn-back"
                                onClick={() => setStep(1)}
                            >
                                <ArrowLeft size={18} />
                                Voltar
                            </button>

                            <button
                                className="btn-next"
                                onClick={() => setStep(3)}
                            >
                                Próximo
                                <ArrowRight size={18} />
                            </button>
                        </div>
                    </div>
                )}

                {/* PASSO 3 */}
                {step === 3 && (
                    <div className="register-step">
                        <h2>Validação de Identidade</h2>

                        <p className="step-description">
                            Para garantir a segurança da
                            plataforma e liberar seu cadastro,
                            envie documentos legíveis.
                        </p>

                        <div className="form-group">
                            <label>
                                Selecione o Tipo de Documento
                            </label>

                            <div className="input-wrapper">
                                <CreditCard size={18} />

                                <select
                                    value={documentType}
                                    onChange={(e) => setDocumentType(e.target.value)}
                                >
                                    <option value="">Selecione um documento...</option>
                                    <option value="rg">RG (Identidade)</option>
                                    <option value="cnh">CNH</option>
                                    <option value="passaporte">Passaporte</option>
                                </select>
                            </div>
                        </div>

                        <label className="upload-box upload-box--front" htmlFor="doc-front-input">
                            <Upload size={32} />
                            <span>{documentFrontFile ? documentFrontFile.name : "Enviar Frente"}</span>
                            <input id="doc-front-input" type="file" accept="image/*" onChange={handleDocumentFrontChange} />
                        </label>

                        <label className="upload-box upload-box--back" htmlFor="doc-back-input">
                            <Upload size={32} />
                            <span>{documentBackFile ? documentBackFile.name : "Enviar Verso"}</span>
                            <input id="doc-back-input" type="file" accept="image/*" onChange={handleDocumentBackChange} />
                        </label>

                        <div className="step-actions">
                            <button
                                className="btn-back"
                                onClick={() => setStep(2)}
                                disabled={submitting}
                            >
                                <ArrowLeft size={18} />
                                Voltar
                            </button>

                            <button className="btn-submit" onClick={handleRegister} disabled={submitting}>
                                <Check size={18} />
                                {submitting ? "Enviando..." : "Finalizar Cadastro"}
                            </button>
                        </div>
                    </div>
                )}

                <div className="register-footer">
                    <span>Já possui uma conta?</span>

                    <Link to="/">
                        Fazer login
                    </Link>
                </div>
            </div>
            <Toast
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ message: "", type: "success" })}
            />
            {showGoldWelcome && (
                <GoldOnboardingModal
                    trialWelcome
                    onClose={() => navigate("/dashboard-freelancer")}
                />
            )}
        </div>
    );
}